// Deterministic assembler (§8): merged JSONL records → provisional Script v3.
// Two steps: (1) assemble() builds a name-based script skeleton (plain-text
// content; scene-level cast = declared `人物：` ∪ dialogue speakers; declared
// props); (2) assignProvisionalIds() turns names into provisional ids and builds
// the Pass5 evidence roster. pass5-extract augments the name-based scene cast
// between the two steps. Pure and byte-stable. Body before the first scene
// (preamble/outline) is captured as a note, not fatal; a scene that arrives
// before any episode header auto-opens a synthetic episode rather than aborting
// (pass0 strips outline front matter, but a stray scene must never crash).

import type {
  NameStateRef,
  IngestRecord,
  SourceSpan,
  SpeakerRef,
} from "./records.js";

export type ProvisionalOccurrence = { episode: number; scene: number };
export type ProvisionalAsset = {
  provisional_id: string;
  kind: "actor" | "location" | "prop";
  names: string[];
  states: Array<{ name: string; id: string }>; // id is globally unique so merges never collide
  occurrences: ProvisionalOccurrence[];
};

// Name-based intermediate script (pre id-assignment). Loosely typed on purpose.
export type NameScene = {
  identity_key: string;
  scene_id: string;
  environment?: { space?: string; time?: string };
  location: NameStateRef | null;
  actors: NameStateRef[];
  props: NameStateRef[];
  actions: Array<{
    identity_key: string;
    type: string;
    content: string;
    speaker?: SpeakerRef;
    emotion?: string;
    parenthetical?: string;
    extend?: Record<string, unknown>;
  }>;
};
export type NameScript = {
  title: string;
  episodes: Array<{
    identity_key: string;
    episode_id: string;
    title?: string;
    scenes: NameScene[];
  }>;
};
export type AssembleResult = { script: NameScript; notes: Array<{ content: string; category?: string; source: unknown }> };
export interface StructuralIdentityKeys {
  episode: Record<string, string>;
  scene: Record<string, string>;
  action: Record<string, string>;
}

const pad = (n: number): string => String(n).padStart(3, "0");
const norm = (s: string): string => s.normalize("NFC").replace(/\s+/g, " ").trim();

export function assemble(records: IngestRecord[], opts: { title?: string } = {}): AssembleResult {
  const episodes: NameScript["episodes"] = [];
  const notes: AssembleResult["notes"] = [];
  let curEpisode: NameScript["episodes"][number] | null = null;
  let curScene: NameScene | null = null;
  let epNo = 0;
  let scnNo = 0;
  const identityOccurrences = new Map<string, number>();
  const sourceIdentityKey = (kind: string, source: SourceSpan): string => {
    const base = `text:${kind}:${source.chunk}:${source.line_start}:${source.line_end}`;
    const occurrence = (identityOccurrences.get(base) ?? 0) + 1;
    identityOccurrences.set(base, occurrence);
    return `${base}:${occurrence}`;
  };

  const addActor = (scene: NameScene, ref: NameStateRef) => {
    const existing = scene.actors.find((a) => norm(a.name) === norm(ref.name));
    if (!existing) scene.actors.push(ref);
    else if (ref.state && !existing.state) existing.state = ref.state;
  };

  for (const rec of records) {
    switch (rec.kind) {
      case "episode": {
        epNo++;
        curEpisode = {
          identity_key: sourceIdentityKey("episode", rec.source),
          episode_id: `ep_${pad(epNo)}`,
          scenes: [],
        };
        if (rec.title !== undefined) curEpisode.title = rec.title;
        episodes.push(curEpisode);
        curScene = null;
        break;
      }
      case "scene": {
        if (!curEpisode) {
          // A scene with no preceding episode header (stray outline scene, or a
          // screenplay that opens directly on a scene): auto-open episode 1 so
          // the content is kept and structured instead of crashing the ingest.
          epNo++;
          curEpisode = {
            identity_key: sourceIdentityKey("synthetic-episode", rec.source),
            episode_id: `ep_${pad(epNo)}`,
            scenes: [],
          };
          episodes.push(curEpisode);
        }
        scnNo++;
        curScene = {
          identity_key: sourceIdentityKey("scene", rec.source),
          scene_id: `scn_${pad(scnNo)}`,
          location: rec.location ? { ...rec.location } : null,
          actors: (rec.actors ?? []).map((a) => ({ ...a })),
          props: (rec.props ?? []).map((p) => ({ ...p })),
          actions: [],
        };
        if (rec.environment) curScene.environment = rec.environment;
        curEpisode.scenes.push(curScene);
        break;
      }
      case "note":
        notes.push({ content: rec.content, ...(rec.category ? { category: rec.category } : {}), source: rec.source });
        break;
      case "action":
      case "transition": {
        // Leading preamble (title block / 策划大纲 / 项目信息 before the first
        // scene) normalizes to body records with no scene to hang on. Keep it as
        // a note instead of aborting the whole ingest.
        if (!curScene) { notes.push({ content: rec.content, category: "preamble", source: rec.source }); break; }
        curScene.actions.push({
          identity_key: sourceIdentityKey(rec.kind, rec.source),
          type: rec.kind,
          content: rec.content,
        });
        break;
      }
      case "dialogue":
      case "inner_thought": {
        if (!curScene) { notes.push({ content: rec.content, category: "preamble", source: rec.source }); break; }
        const action: NameScene["actions"][number] = {
          identity_key: sourceIdentityKey(rec.kind, rec.source),
          type: rec.kind,
          content: rec.content,
          speaker: rec.speaker,
        };
        if (rec.emotion !== undefined) action.emotion = rec.emotion;
        curScene.actions.push(action);
        // An actor speaker is present in the scene.
        if ("name" in rec.speaker) addActor(curScene, rec.speaker.state !== undefined ? { name: rec.speaker.name, state: rec.speaker.state } : { name: rec.speaker.name });
        break;
      }
    }
  }

  return { script: { title: opts.title ?? "", episodes }, notes };
}

class Registry {
  private byNorm = new Map<string, ProvisionalAsset>();
  private order: ProvisionalAsset[] = [];
  constructor(private prefix: string, private kind: ProvisionalAsset["kind"], private nextStateId: () => string) {}
  ensure(name: string, occ: ProvisionalOccurrence): ProvisionalAsset {
    const key = norm(name);
    let a = this.byNorm.get(key);
    if (!a) { a = { provisional_id: `${this.prefix}_${pad(this.order.length + 1)}`, kind: this.kind, names: [], states: [], occurrences: [] }; this.byNorm.set(key, a); this.order.push(a); }
    if (!a.names.includes(name)) a.names.push(name);
    a.occurrences.push(occ);
    return a;
  }
  stateId(a: ProvisionalAsset, state: string): string {
    const key = norm(state);
    const existing = a.states.find((s) => norm(s.name) === key);
    if (existing) return existing.id;
    const id = this.nextStateId();
    a.states.push({ name: state, id });
    return id;
  }
  list(): ProvisionalAsset[] { return this.order; }
}

function emitStates(a: ProvisionalAsset): Array<{ state_id: string; state_name: string; description: string }> {
  const out = [{ state_id: "default", state_name: "default", description: "" }];
  for (const s of a.states) out.push({ state_id: s.id, state_name: s.name, description: "" });
  return out;
}

// Deterministically assign provisional ids to a (possibly extract-augmented)
// name-based script; returns the provisional v3 doc + evidence roster.
export function assignProvisionalIds(name: NameScript): {
  script: Record<string, unknown>;
  assets: ProvisionalAsset[];
  identityKeys: StructuralIdentityKeys;
} {
  let stateSeq = 0;
  const nextStateId = (): string => `st_${pad(++stateSeq)}`; // globally unique across all assets
  const actors = new Registry("act", "actor", nextStateId);
  const locations = new Registry("loc", "location", nextStateId);
  const props = new Registry("prp", "prop", nextStateId);
  const identityKeys: StructuralIdentityKeys = {
    episode: {},
    scene: {},
    action: {},
  };
  const seenIdentityKeys: Record<keyof StructuralIdentityKeys, Set<string>> = {
    episode: new Set(),
    scene: new Set(),
    action: new Set(),
  };
  const bindIdentity = (
    kind: keyof StructuralIdentityKeys,
    address: string,
    identityKey: string,
  ): void => {
    if (!identityKey || seenIdentityKeys[kind].has(identityKey)) {
      throw new Error(`Duplicate or missing ${kind} source identity: ${identityKey}`);
    }
    seenIdentityKeys[kind].add(identityKey);
    identityKeys[kind][address] = identityKey;
  };

  const idState = (reg: Registry, ref: NameStateRef, occ: ProvisionalOccurrence, idKey: string): Record<string, unknown> => {
    const a = reg.ensure(ref.name, occ);
    const out: Record<string, unknown> = { [idKey]: a.provisional_id };
    if (ref.state !== undefined) out.state_id = reg.stateId(a, ref.state);
    return out;
  };

  const episodes = name.episodes.map((ep, ei) => {
    bindIdentity("episode", ep.episode_id, ep.identity_key);
    return {
      episode_id: ep.episode_id,
      ...(ep.title ? { title: ep.title } : {}),
      scenes: ep.scenes.map((sc, si) => {
        const sceneAddress = `${ep.episode_id}/${sc.scene_id}`;
        bindIdentity("scene", sceneAddress, sc.identity_key);
        const occ: ProvisionalOccurrence = { episode: ei, scene: si };
        const location = sc.location ? idState(locations, sc.location, occ, "location_id") : null;
        const sceneActors = sc.actors.map((a) => idState(actors, a, occ, "actor_id"));
        const sceneProps = sc.props.map((p) => idState(props, p, occ, "prop_id"));
        const actions = sc.actions.map((ac, actionIndex) => {
          bindIdentity("action", `${sceneAddress}#${actionIndex}`, ac.identity_key);
          const out: Record<string, unknown> = { type: ac.type, content: ac.content };
          if (ac.speaker) {
            if ("name" in ac.speaker) {
              const a = actors.ensure(ac.speaker.name, occ);
              const sp: Record<string, unknown> = { actor_id: a.provisional_id };
              if (ac.speaker.state !== undefined) sp.state_id = actors.stateId(a, ac.speaker.state);
              out.speaker = sp;
            } else {
              out.speaker = ac.speaker.label !== undefined
                ? { kind: ac.speaker.kind, label: ac.speaker.label }
                : { kind: ac.speaker.kind };
            }
          }
          if (ac.type === "dialogue" || ac.type === "inner_thought") {
            // The V3 bridge only has `emotion`; Script v4's parenthetical is
            // translated to that legacy field immediately before V4 conversion.
            if (ac.parenthetical !== undefined) out.emotion = ac.parenthetical;
            else if (ac.emotion !== undefined) out.emotion = ac.emotion;
          }
          // Carry through free-form extend (e.g. video timestamps) so it survives
          // into the final v3 product; text ingest never sets it.
          if (ac.extend !== undefined) out.extend = ac.extend;
          return out;
        });
        return {
          scene_id: sc.scene_id,
          ...(sc.environment ? { environment: sc.environment } : {}),
          location,
          actors: sceneActors,
          props: sceneProps,
          actions,
        };
      }),
    };
  });

  const mkAssets = (reg: Registry, idKey: string, nameKey: string) => reg.list().map((a) => ({ [idKey]: a.provisional_id, [nameKey]: a.names[0] ?? a.provisional_id, aliases: a.names.slice(1), description: "", states: emitStates(a) }));

  const script: Record<string, unknown> = {
    version: 3,
    title: name.title,
    worldview: "",
    style: "",
    actors: mkAssets(actors, "actor_id", "actor_name"),
    locations: mkAssets(locations, "location_id", "location_name"),
    props: mkAssets(props, "prop_id", "prop_name"),
    episodes,
  };
  return {
    script,
    assets: [...actors.list(), ...locations.list(), ...props.list()],
    identityKeys,
  };
}
